
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { 
  ExternalLink, 
  Users, 
  Wallet, 
  FileText, 
  VoteIcon, 
  CoinsIcon, 
  BarChart3, 
  ArrowUpRightSquare,
  Plus,
  Loader2,
} from "lucide-react";
import { useGovernorProposals } from "./hooks/useGovernorProposals";
import { Address, formatEther, formatUnits, parseAbiItem, parseEther, parseUnits } from "viem";
import { CreateProposalDialog } from "./proposals/CreateProposalDialog";
import { ProposalCard } from "./proposals/ProposalCard";
import { PendingProposalTransactions } from "./proposals/PendingProposalTransactions";
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { polygonArchiveRpcClients } from "@/lib/polygonRpc";
import { polygon } from "viem/chains";
import { getDescendingBlockRanges, getHistoryStartBlock, getLogsWithAdaptiveRange, waitForNextLogBatch } from "./utils/chainHistory";
import { useToast } from "@/components/ui/use-toast";

const TRANSFER_EVENT = parseAbiItem("event Transfer(address indexed from, address indexed to, uint256 value)");
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

const getTransactionError = (error: unknown) => {
  if (!error || typeof error !== "object") return "Transaction was rejected.";
  const candidate = error as { shortMessage?: string; message?: string };
  return candidate.shortMessage || candidate.message || "Transaction was rejected.";
};

// Interface for DAO data
interface DaoData {
  name: string;
  symbol: string;
  creatorAddress: string;
  governorAddress: string;
  tokenAddress: string;
  treasuryAddress: string;
  createdAt: string;
  chainId: number;
  chainName: string;
}

interface DaoDetailsProps {
  dao: DaoData;
}

interface TokenTransferLog {
  args: {
    from?: Address;
    to?: Address;
  };
}

export const DaoDetails = ({ dao }: DaoDetailsProps) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [showCreateProposal, setShowCreateProposal] = useState(false);
  const [contributionAmount, setContributionAmount] = useState("");
  const [tokenContributionAmount, setTokenContributionAmount] = useState("");
  const [isContributingNative, setIsContributingNative] = useState(false);
  const [isContributingToken, setIsContributingToken] = useState(false);
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient({ chainId: polygon.id });
  const { toast } = useToast();
  
  const {
    proposals,
    pendingProposalTransactions,
    dismissPendingProposal,
    isLoadingProposals,
    createProposal,
    isProposeLoading,
    proposalStatus,
    vote,
    isVoteLoading,
    queueProposal,
    executeProposal,
    activeLifecycleAction,
    isLifecycleLoading,
    proposalCount,
    proposalError,
  } = useGovernorProposals(dao.governorAddress as Address, dao.createdAt);
  
  const erc20Abi = [
    parseAbiItem("function balanceOf(address account) view returns (uint256)"),
    parseAbiItem("function decimals() view returns (uint8)"),
    parseAbiItem("function transfer(address to, uint256 amount) returns (bool)"),
  ] as const;

  const { data: treasuryData, isLoading: isLoadingTreasury, refetch: refetchTreasury } = useQuery({
    queryKey: ["dao-treasury", dao.treasuryAddress, dao.tokenAddress],
    queryFn: async () => {
      if (!publicClient) throw new Error("Blockchain client unavailable");
      const [maticBalance, tokenBalance, decimals] = await Promise.all([
        publicClient.getBalance({ address: dao.treasuryAddress as Address }),
        publicClient.readContract({ address: dao.tokenAddress as Address, abi: erc20Abi, functionName: "balanceOf", args: [dao.treasuryAddress as Address] } as never) as Promise<bigint>,
        publicClient.readContract({ address: dao.tokenAddress as Address, abi: erc20Abi, functionName: "decimals" } as never).catch(() => 18) as Promise<number>,
      ]);
      return { maticBalance, tokenBalance, decimals: Number(decimals) };
    },
    enabled: Boolean(publicClient && dao.treasuryAddress && dao.tokenAddress),
  });

  const { data: members = [], isLoading: isLoadingMembers, isError: isMembersError } = useQuery({
    queryKey: ["dao-members", dao.tokenAddress, dao.creatorAddress, dao.createdAt],
    queryFn: async () => {
      if (!publicClient) throw new Error("Blockchain client unavailable");
      const latestBlock = await publicClient.getBlockNumber();
      const earliestBlock = getHistoryStartBlock(latestBlock, dao.createdAt);
      const ranges = getDescendingBlockRanges(latestBlock, earliestBlock);
      const logs: TokenTransferLog[] = [];

      const logQueryConcurrency = polygonArchiveRpcClients.length;
      for (let index = 0; index < ranges.length; index += logQueryConcurrency) {
        const batch = ranges.slice(index, index + logQueryConcurrency);
        const chunks = await Promise.all(batch.map(({ fromBlock, toBlock }, batchIndex) => {
          const logClient = polygonArchiveRpcClients[(index + batchIndex) % logQueryConcurrency];
          return getLogsWithAdaptiveRange(fromBlock, toBlock, (adaptiveFromBlock, adaptiveToBlock) => logClient.getLogs({
            address: dao.tokenAddress as Address,
            event: TRANSFER_EVENT,
            fromBlock: adaptiveFromBlock,
            toBlock: adaptiveToBlock,
          }));
        }));
        logs.push(...chunks.flat() as unknown as TokenTransferLog[]);
        if (index + logQueryConcurrency < ranges.length) {
          await waitForNextLogBatch();
        }
      }

      const holders = new Set<string>([dao.creatorAddress.toLowerCase()]);
      for (const log of logs) {
        if (log.args.from && log.args.from.toLowerCase() !== ZERO_ADDRESS) holders.add(log.args.from.toLowerCase());
        if (log.args.to && log.args.to.toLowerCase() !== ZERO_ADDRESS) holders.add(log.args.to.toLowerCase());
      }
      const balances = await Promise.all([...holders].map(async (holder) => ({
        address: holder,
        balance: await publicClient.readContract({ address: dao.tokenAddress as Address, abi: erc20Abi, functionName: "balanceOf", args: [holder as Address] } as never) as bigint,
      })));
      return balances.filter((holder) => holder.balance > 0n).sort((a, b) => Number(b.balance - a.balance)).slice(0, 100);
    },
    enabled: activeTab === "members" && Boolean(publicClient && dao.tokenAddress),
    retry: 1,
    staleTime: 30_000,
  });

  const handleContribute = async () => {
    if (!isConnected || !address || !walletClient || !publicClient) return toast({ title: "Connect your wallet", description: "Connect a wallet before contributing.", variant: "destructive" });
    if (!contributionAmount || Number(contributionAmount) <= 0) return toast({ title: "Enter an amount", description: "Enter a positive POL amount to contribute.", variant: "destructive" });
    setIsContributingNative(true);
    try {
      if (await walletClient.getChainId() !== polygon.id) throw new Error("Switch your wallet to Polygon before contributing.");
      const hash = await walletClient.sendTransaction({ account: address, to: dao.treasuryAddress as Address, value: parseEther(contributionAmount), chain: polygon } as never) as `0x${string}`;
      toast({ title: "Contribution submitted", description: "Waiting for Polygon confirmation..." });
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      if (receipt.status === "reverted") throw new Error("The POL contribution reverted on Polygon.");
      setContributionAmount("");
      await refetchTreasury();
      toast({ title: "Contribution confirmed", description: "The treasury's native-token balance has been updated." });
    } catch (error: unknown) {
      toast({ title: "Contribution failed", description: getTransactionError(error), variant: "destructive" });
    } finally {
      setIsContributingNative(false);
    }
  };

  const handleTokenContribute = async () => {
    if (!isConnected || !address || !walletClient || !publicClient) {
      toast({ title: "Connect your wallet", description: "Connect a Polygon wallet before contributing.", variant: "destructive" });
      return;
    }

    setIsContributingToken(true);
    try {
      if (await walletClient.getChainId() !== polygon.id) throw new Error("Switch your wallet to Polygon before contributing.");
      const decimals = treasuryData?.decimals ?? Number(await publicClient.readContract({
        address: dao.tokenAddress as Address,
        abi: erc20Abi,
        functionName: "decimals",
      } as never));
      const amount = parseUnits(tokenContributionAmount, decimals);
      if (amount <= 0n) throw new Error(`Enter a positive ${dao.symbol} amount.`);

      const walletBalance = await publicClient.readContract({
        address: dao.tokenAddress as Address,
        abi: erc20Abi,
        functionName: "balanceOf",
        args: [address],
      } as never) as bigint;
      if (amount > walletBalance) {
        throw new Error(`Insufficient ${dao.symbol} balance. Available: ${formatUnits(walletBalance, decimals)} ${dao.symbol}.`);
      }

      await publicClient.simulateContract({
        address: dao.tokenAddress as Address,
        abi: erc20Abi,
        functionName: "transfer",
        args: [dao.treasuryAddress as Address, amount],
        account: address,
      } as never);
      const hash = await walletClient.writeContract({
        address: dao.tokenAddress as Address,
        abi: erc20Abi,
        functionName: "transfer",
        args: [dao.treasuryAddress as Address, amount],
        account: address,
        chain: polygon,
        gas: 150_000n,
      } as never) as `0x${string}`;
      toast({ title: "Token contribution submitted", description: "Waiting for Polygon confirmation..." });
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      if (receipt.status !== "success") throw new Error(`The ${dao.symbol} contribution reverted on Polygon.`);

      setTokenContributionAmount("");
      await refetchTreasury();
      toast({ title: "Token contribution confirmed", description: `${formatUnits(amount, decimals)} ${dao.symbol} was transferred to the DAO treasury.` });
    } catch (error: unknown) {
      toast({ title: "Token contribution failed", description: getTransactionError(error), variant: "destructive" });
    } finally {
      setIsContributingToken(false);
    }
  };
  
  // Format address for display
  const formatAddress = (address: string) => {
    if (!address || address.length < 10) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Get block explorer URL based on chainId
  const getBlockExplorerUrl = (chainId: number) => {
    switch (chainId) {
      case 1: return "https://etherscan.io";
      case 137: return "https://polygonscan.com";
      case 100: return "https://gnosisscan.io";
      case 10: return "https://optimistic.etherscan.io";
      case 42161: return "https://arbiscan.io";
      default: return "https://etherscan.io";
    }
  };
  
  const blockExplorerUrl = getBlockExplorerUrl(dao.chainId);

  return (
    <div className="space-y-6">
      {/* DAO Header */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <CardTitle>{dao.name}</CardTitle>
                <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
                  {dao.symbol}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                Created on {new Date(dao.createdAt).toLocaleDateString()} • {dao.chainName}
              </div>
            </div>
            <Button variant="outline" size="sm" asChild>
              <a 
                href="https://snapshot.box/#/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5"
              >
                <span>Open in Snapshot</span>
                <ArrowUpRightSquare className="h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex flex-col">
              <span className="text-muted-foreground">Proposals</span>
              <div className="flex items-center gap-1 font-medium">
                <FileText className="w-4 h-4 text-muted-foreground" />
                {proposalCount}
              </div>
            </div>
          </div>
          
          <Separator className="my-4" />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Token</span>
                <a 
                  href={`${blockExplorerUrl}/address/${dao.tokenAddress}`}
                  target="_blank"
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  {formatAddress(dao.tokenAddress)}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Governor</span>
                <a 
                  href={`${blockExplorerUrl}/address/${dao.governorAddress}`}
                  target="_blank"
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  {formatAddress(dao.governorAddress)}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
            
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Treasury</span>
                <a 
                  href={`${blockExplorerUrl}/address/${dao.treasuryAddress}`}
                  target="_blank"
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  {formatAddress(dao.treasuryAddress)}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* DAO Tabs */}
      <Tabs defaultValue="overview" className="w-full" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="proposals">Proposals</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="contribute">Contribute</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="mt-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Treasury Assets</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingTreasury ? <div className="py-6 text-center text-muted-foreground">Loading treasury balances...</div> : treasuryData ? (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <CoinsIcon className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">POL</div>
                          <div className="text-sm text-muted-foreground">Native treasury balance</div>
                        </div>
                      </div>
                      <div className="font-medium">{formatEther(treasuryData.maticBalance)} POL</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center"><CoinsIcon className="h-4 w-4 text-primary" /></div><div><div className="font-medium">{dao.symbol}</div><div className="text-sm text-muted-foreground">Governance token balance</div></div></div>
                      <div className="font-medium">{formatUnits(treasuryData.tokenBalance, treasuryData.decimals)}</div>
                    </div>
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  No assets in treasury
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6 text-muted-foreground">
                No recent activity to display
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="proposals" className="mt-0 space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Proposals</h3>
            <Button onClick={() => setShowCreateProposal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Proposal
            </Button>
          </div>

          <PendingProposalTransactions
            transactions={pendingProposalTransactions}
            blockExplorerUrl={blockExplorerUrl}
            onDismiss={dismissPendingProposal}
          />
          
          {isLoadingProposals ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-6 text-muted-foreground">
                  Loading proposals...
                </div>
              </CardContent>
            </Card>
          ) : proposalError ? (
            <Card>
              <CardContent className="pt-6">
                <div className="py-8 text-center text-destructive">
                  Unable to load on-chain proposals from Polygon.
                  <p className="mt-2 text-xs text-muted-foreground">{proposalError}</p>
                </div>
              </CardContent>
            </Card>
          ) : proposals.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12 px-4">
                  <FileText className="mx-auto h-12 w-12 text-muted-foreground/30" />
                  <h3 className="mt-4 text-lg font-medium">No Proposals Yet</h3>
                  <p className="mt-2 text-muted-foreground max-w-md mx-auto">
                    Be the first to create a proposal for this DAO. Proposals allow members to vote on important decisions.
                  </p>
                  <Button className="mt-6" onClick={() => setShowCreateProposal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Proposal
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            proposals.map((proposal) => (
              <ProposalCard
                key={proposal.id}
                proposal={proposal}
                onVote={vote}
                onQueue={queueProposal}
                onExecute={executeProposal}
                isVoteLoading={isVoteLoading}
                isLifecycleLoading={isLifecycleLoading}
                activeLifecycleAction={activeLifecycleAction}
              />
            ))
          )}

          <CreateProposalDialog
            open={showCreateProposal}
            onOpenChange={setShowCreateProposal}
            onCreateProposal={createProposal}
            isLoading={isProposeLoading}
            statusLabel={proposalStatus}
            governorAddress={dao.governorAddress as Address}
            treasuryAddress={dao.treasuryAddress as Address}
            tokenAddress={dao.tokenAddress as Address}
            tokenSymbol={dao.symbol}
          />
        </TabsContent>
        
        <TabsContent value="members" className="mt-0">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12 px-4">
                <Users className="mx-auto h-12 w-12 text-muted-foreground/30" />
                <h3 className="mt-4 text-lg font-medium">Governance Token Members</h3>
                <p className="mt-2 text-muted-foreground max-w-md mx-auto">
                  Members are derived from governance-token transfer history. Showing the top active holders.
                </p>
                {isLoadingMembers ? <p className="mt-6 text-muted-foreground">Loading token holders...</p> : isMembersError ? <p className="mt-6 text-destructive">Unable to load token holders from Polygon.</p> : members.length > 0 ? <div className="mt-6 space-y-2 text-left">{members.map((member, index) => <div key={member.address} className="flex items-center justify-between rounded border p-3 text-sm"><span><span className="mr-2 text-muted-foreground">#{index + 1}</span><a className="font-mono text-primary hover:underline" href={`${blockExplorerUrl}/address/${member.address}`} target="_blank" rel="noreferrer">{formatAddress(member.address)}</a></span><span className="font-medium">{formatUnits(member.balance, treasuryData?.decimals ?? 18)} {dao.symbol}</span></div>)}</div> : <p className="mt-6 text-muted-foreground">No active governance token holders found.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="contribute" className="mt-0">
          <Card>
            <CardContent className="pt-6">
              <div className="py-8 px-4">
                <CoinsIcon className="mx-auto h-12 w-12 text-muted-foreground/30" />
                <h3 className="mt-4 text-center text-lg font-medium">Contribute to DAO Treasury</h3>
                <p className="mt-2 text-center text-muted-foreground max-w-lg mx-auto">
                  Transfer native POL or governance tokens directly to the treasury on Polygon.
                </p>

                <div className="mx-auto mt-8 grid max-w-2xl gap-6 md:grid-cols-2">
                  <div className="rounded-lg border p-4">
                    <h4 className="font-medium">Native POL</h4>
                    <p className="mt-1 text-sm text-muted-foreground">Fund treasury gas and native-token proposals.</p>
                    <div className="mt-4 space-y-3">
                      <Input type="number" min="0" step="0.001" placeholder="Amount in POL" value={contributionAmount} onChange={(event) => setContributionAmount(event.target.value)} />
                      <Button className="w-full" onClick={handleContribute} disabled={isContributingNative || isContributingToken}>
                        {isContributingNative && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Contribute POL
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4">
                    <h4 className="font-medium">Governance token ({dao.symbol})</h4>
                    <p className="mt-1 text-sm text-muted-foreground">Send governance tokens for the DAO to control through proposals.</p>
                    <div className="mt-4 space-y-3">
                      <Input type="number" min="0" step="any" placeholder={`Amount in ${dao.symbol}`} value={tokenContributionAmount} onChange={(event) => setTokenContributionAmount(event.target.value)} />
                      <Button className="w-full" onClick={handleTokenContribute} disabled={isContributingToken || isContributingNative || !tokenContributionAmount}>
                        {isContributingToken && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Contribute {dao.symbol}
                      </Button>
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">
                      Contributed tokens leave your wallet and reduce your voting power. Treasury spending requires a successful governance proposal.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
